import { useState, useEffect } from 'react';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'forbidden' | 'error';

interface AdminUser {
  email: string;
  userId: string;
}

interface UseAdminAuthResult {
  status: AuthStatus;
  user: AdminUser | null;
}

const getBaseURL = (): string => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  return '/api';
};

export function useAdminAuth(): UseAdminAuthResult {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AdminUser | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function checkAuth() {
      try {
        const res = await fetch(`${getBaseURL()}/admin/me`, {
          credentials: 'include',
        });

        if (cancelled) return;

        if (res.ok) {
          const data = await res.json();
          setUser({ email: data.email, userId: data.userId });
          setStatus('authenticated');
        } else if (res.status === 401) {
          setStatus('unauthenticated');
        } else if (res.status === 403) {
          setStatus('forbidden');
        } else {
          setStatus('unauthenticated');
        }
      } catch {
        if (!cancelled) {
          setStatus('error');
        }
      }
    }

    checkAuth();
    return () => { cancelled = true; };
  }, []);

  return { status, user };
}
